export type ConsultStatus = "idle" | "dialing" | "active" | null;

export type HoldSlotSnapshotInput = {
  heldCallerNumber?: string | null;
  callerNumber?: string | null;
  backgroundHeldCallUuid?: string | null;
  callUuid?: string | null;
  callId?: string | null;
  backgroundHeldCallReference?: string | null;
  callReference?: string | null;
};

export type ConsultSlotSnapshot = {
  heldCallerNumber: string | null;
  backgroundHeldCallUuid: string | null;
  backgroundHeldCallReference: string | null;
  consultNumber: string;
  consultStatus: "dialing";
};

export type SwappedConsultSlots = {
  callerNumber: string | null;
  heldCallerNumber: string | null;
  callUuid: string | null;
  callReference: string | null;
  isOnHold: false;
  callStatus: "active";
  callDirection: "incoming";
};

export const buildHoldSlotSnapshot = (
  snapshot: HoldSlotSnapshotInput
): Pick<
  HoldSlotSnapshotInput,
  "heldCallerNumber" | "backgroundHeldCallUuid" | "backgroundHeldCallReference"
> => ({
  heldCallerNumber: snapshot.heldCallerNumber ?? snapshot.callerNumber ?? null,
  backgroundHeldCallUuid:
    snapshot.backgroundHeldCallUuid ?? snapshot.callUuid ?? snapshot.callId ?? null,
  backgroundHeldCallReference:
    snapshot.backgroundHeldCallReference ?? snapshot.callReference ?? null,
});

export const buildConsultSlotSnapshot = (
  snapshot: HoldSlotSnapshotInput,
  consultTarget: string
): ConsultSlotSnapshot => {
  const holdSlot = buildHoldSlotSnapshot(snapshot);

  return {
    heldCallerNumber: holdSlot.heldCallerNumber ?? snapshot.callerNumber ?? null,
    backgroundHeldCallUuid: holdSlot.backgroundHeldCallUuid ?? null,
    backgroundHeldCallReference: holdSlot.backgroundHeldCallReference ?? null,
    consultNumber: consultTarget,
    consultStatus: "dialing",
  };
};

export const buildSwappedConsultSlots = (
  snapshot: HoldSlotSnapshotInput & {
    consultNumber?: string | null;
    consultStatus?: ConsultStatus;
  }
): SwappedConsultSlots => {
  const foregroundNumber = snapshot.heldCallerNumber ?? snapshot.callerNumber ?? null;
  const heldNumber =
    snapshot.consultNumber ?? snapshot.callerNumber ?? snapshot.heldCallerNumber ?? null;

  return {
    callerNumber: foregroundNumber,
    heldCallerNumber: heldNumber,
    callUuid: snapshot.backgroundHeldCallUuid ?? snapshot.callUuid ?? null,
    callReference:
      snapshot.backgroundHeldCallReference ?? snapshot.callReference ?? null,
    isOnHold: false,
    callStatus: "active",
    callDirection: "incoming",
  };
};

export const clearConsultSlotFields = () => ({
  consultNumber: null,
  consultStatus: null,
  heldCallerNumber: null,
  backgroundHeldCallUuid: null,
  backgroundHeldCallReference: null,
});

export const isConsultDialBlocked = (consultStatus: ConsultStatus | undefined) =>
  consultStatus === "dialing" || consultStatus === "active";

export const isActiveConsultSession = (state: {
  backgroundHeldCallUuid?: string | null;
  consultStatus?: ConsultStatus;
}) =>
  Boolean(state.backgroundHeldCallUuid) && isConsultDialBlocked(state.consultStatus);

/**
 * Whether an outbound dial is being placed from behind a call on hold.
 *
 * The phone's own hold state counts as well as call state. Call state only catches up a render
 * after `hold()` resolves, so a dial placed straight after holding (AC-328) was refused as if
 * the call were still live, even though the phone had already parked it.
 */
export const isDialingFromHeldCall = (input: {
  isOnHold: boolean;
  callStatus: string;
  hasHeldBackgroundSession: boolean;
  isForegroundSessionOnHold: boolean;
}) =>
  input.isOnHold ||
  input.callStatus === "held" ||
  input.hasHeldBackgroundSession ||
  input.isForegroundSessionOnHold;
